import { useMemo } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useHistory, useUsers } from '../api/hooks.ts'
import { useAuth } from '../auth/AuthContext.tsx'
import { Card, ErrorNote, Screen, SectionTitle, Spinner } from '../components/ui.tsx'
import { WeeklyBars } from '../components/WeeklyBars.tsx'
import { eur, shortDate } from '../lib/format.ts'

/**
 * Historial de semanas cobradas.
 *
 * El gráfico no busca precisión analítica sino una lectura de un vistazo: la
 * línea de la asignación completa marca el listón, y cada barra se ve por encima
 * o por debajo. Para un adolescente eso comunica mucho más que una tabla.
 */
export function HistoryScreen() {
  const { user } = useAuth()
  const params = useParams<{ userId?: string }>()
  const targetId = params.userId ?? user?.id

  const history = useHistory(params.userId)
  const users = useUsers()

  const rows = useMemo(
    () => [...(history.data ?? [])].reverse().filter((w) => w.payoutCents !== null),
    [history.data],
  )

  const chart = useMemo(
    () =>
      rows.slice(-12).map((w) => ({
        label: shortDate(w.weekStart),
        cents: w.payoutCents ?? 0,
        baseCents: w.baseAmountCents,
      })),
    [rows],
  )

  if (history.isLoading) return <Spinner />
  if (history.error) return <Screen><ErrorNote error={history.error} /></Screen>

  const name = users.data?.find((u) => u.id === targetId)?.name ?? user?.name ?? ''
  const total = rows.reduce((a, w) => a + (w.payoutCents ?? 0), 0)
  const average = rows.length > 0 ? Math.round(total / rows.length) : 0

  return (
    <Screen>
      <h1 className="mb-1 text-xl font-bold tracking-tight">Historial</h1>
      <p className="mb-4 text-sm text-[var(--color-muted)]">{name}</p>

      {rows.length === 0 ? (
        <Card>
          <p className="py-2 text-sm text-[var(--color-muted)]">
            Todavía no hay ninguna semana cerrada. Aparecerán aquí a partir del primer viernes.
          </p>
        </Card>
      ) : (
        <>
          <Card>
            <div className="flex justify-between">
              <div>
                <p className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Media</p>
                <p className="text-xl font-bold tabular-nums">{eur(average)}</p>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-wide text-[var(--color-muted)]">
                  Total cobrado
                </p>
                <p className="text-xl font-bold tabular-nums">{eur(total)}</p>
              </div>
            </div>

            <div className="mt-4">
              <WeeklyBars data={chart} />
            </div>
          </Card>

          <SectionTitle>Semanas</SectionTitle>
          <Card>
            {[...rows].reverse().map((w) => (
              <Link
                key={w.id}
                to={`/semana/${w.id}/desglose`}
                className="flex items-center justify-between border-b border-[var(--color-line)] py-3 last:border-b-0"
              >
                <div>
                  <p className="text-[15px]">
                    {shortDate(w.weekStart)} – {shortDate(w.weekEnd)}
                  </p>
                  <p className="text-xs text-[var(--color-muted)]">
                    {w.payoutCents === w.baseAmountCents
                      ? 'Semana completa'
                      : `${eur((w.payoutCents ?? 0) - w.baseAmountCents)} respecto a la asignación`}
                  </p>
                </div>
                <span className="tabular-nums font-semibold">{eur(w.payoutCents ?? 0)}</span>
              </Link>
            ))}
          </Card>
        </>
      )}
    </Screen>
  )
}
